import type { Inspection } from '@/services/inspection.service';

/**
 * D-129 — pure UI rules for the job-level quotation workflow gates. The
 * backend enforces every one of these; the page uses them only to decide what
 * to render and how to explain a disabled action. Kept free of React so they
 * can be unit-tested directly.
 */

export type QuotationSendGate =
  { allowed: true; reason: null } | { allowed: false; reason: string };

type GateInput = Pick<
  Inspection,
  | 'operationsReviewStatus'
  | 'commercialReviewStatus'
  | 'customerApprovalStatus'
  | 'quotationDocumentId'
  | 'workOrderReviewStatus'
>;

/** May Commercial attach/replace/remove the quotation PDF right now?
 * Mirrors `assertQuotationDocumentEditable` on the server. */
export function canEditQuotationDocument(i: GateInput): boolean {
  if (i.operationsReviewStatus !== 'APPROVED_FOR_COMMERCIAL') return false;
  return (
    i.commercialReviewStatus === null ||
    i.commercialReviewStatus === undefined ||
    i.commercialReviewStatus === 'PENDING' ||
    i.customerApprovalStatus === 'MODIFICATION_REQUESTED'
  );
}

/** May "Send to Customer" be submitted? Requires the attached quotation PDF. */
export function quotationSendGate(i: GateInput): QuotationSendGate {
  if (!canEditQuotationDocument(i)) {
    return { allowed: false, reason: 'The quotation is not waiting for Commercial right now.' };
  }
  if (!i.quotationDocumentId) {
    return {
      allowed: false,
      reason: 'Attach the quotation PDF before sending to the customer.',
    };
  }
  return { allowed: true, reason: null };
}

/** Is a customer-submitted Work Order waiting for Commercial acceptance? */
export function isWorkOrderAwaitingAcceptance(i: GateInput): boolean {
  return (
    i.customerApprovalStatus === 'APPROVED' &&
    i.workOrderReviewStatus === 'PENDING_COMMERCIAL_REVIEW'
  );
}

/**
 * Staff-recorded customer decisions (the `customer-approvals` fallback).
 * Reject and Request Modification are offered whenever the endpoints accept
 * them — the quotation is with the customer (`SENT_TO_CUSTOMER`), regardless
 * of an earlier answer this round, exactly as before D-129. Approve is only
 * offered while the round is unanswered (`PENDING`), because it now carries
 * the Work Order and the server refuses it otherwise.
 */
export function staffCustomerDecisionActions(
  i: Pick<Inspection, 'commercialReviewStatus' | 'customerApprovalStatus'>,
  hasCustomerApprovalPermission: boolean,
): { approve: boolean; reject: boolean; requestModification: boolean } {
  const withCustomer =
    hasCustomerApprovalPermission && i.commercialReviewStatus === 'SENT_TO_CUSTOMER';
  return {
    approve: withCustomer && i.customerApprovalStatus === 'PENDING',
    reject: withCustomer,
    requestModification: withCustomer,
  };
}

/** Client-side PDF check for the file picker. The server re-validates MIME,
 * signature and size; this only gives immediate feedback. */
export function isPdfFile(file: Pick<File, 'name' | 'type'>): boolean {
  if (file.type === 'application/pdf') return true;
  return file.type === '' && /\.pdf$/i.test(file.name);
}
